"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";

import CustomImage from "@/components/shared/CustomImage";
import { Typography } from "@/components/shared/Typography";
import { getLabel } from "@/data/labels";
import { SEARCH_RESULT_NEWS_LIMIT } from "@/data/constants";
import { fetchInterviewsByReporter } from "@/services/commonService";
import { ArticleType } from "@/types/article";
import { getImageUrl } from "@/utils/getImageUrl";
import SocialShare from "@/components/shared/SocialShare";
import { IoMdShare } from "react-icons/io";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getCategoryDisplayName, getLocalizedString } from "@/utils/getLocalizedString";
import { formatDate } from "@/utils/dateUtils";

interface AuthorInterviewsListSectionProps {
  initialData: {
    docs: ArticleType[];
    totalDocs: number;
    limit: number;
    page: number;
  };
  slug: string;
  lang: AppLocale;
}

export default function AuthorInterviewsListSection({ initialData, slug, lang }: AuthorInterviewsListSectionProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "en-IN";
  const [articles, setArticles] = useState<ArticleType[]>(initialData?.docs || []);
  const [filters, setFilters] = useState({
    limit: SEARCH_RESULT_NEWS_LIMIT,
    page: 1,
  });

  console.log(articles)

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["fetchInterviewsByReporter", filters.page, slug],
    enabled: filters.page > 1,
    queryFn: () => fetchInterviewsByReporter({ limit: filters?.limit, page: filters?.page, slug, lang }),
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data) {
      setArticles(prev => [...prev, ...(data?.data?.docs || [])]);
    }
  }, [data]);

  // Determine if we should show the Load More button
  const showLoadMore =
    articles?.length > 0 &&
    ((filters.page === 1 && initialData.totalDocs > articles.length) ||
      (data && data?.data?.totalDocs > articles.length));

  useEffect(() => {
    setArticles(initialData?.docs || []);
  }, [initialData]);

  return (
    <div>
      {articles?.length > 0 && <h1 className="text-[#3CB128] font-extrabold text-2xl mb-2">Interviews</h1>}
      {articles?.map(({ image, title, slug, categoryId, subCategoryId, publishedAt }, index) => {
        return (
          <div
            key={index}
            className="flex flex-row items-start gap-6 pt-6 pb-6 border-b group first:pt-0 "
          >
            <Link
              href={`/interviews/${slug}`}
              className="w-[30%] min-w-[30%] md:w-[33%] md:min-w-[33%] overflow-hidden rounded-md aspect-[16/12] md:aspect-video"
              aria-label={`View news: ${title}`}
            >
              <CustomImage
                src={getImageUrl(image?.sizes?.ld?.url || image?.url)}
                alt={image?.alt || ""}
                className="object-cover w-full h-full img-zoom-hover"
              />
            </Link>
            <div className="flex-1">
              <Link
                href={`/interviews/${slug}`}
                aria-label={`View news: ${title}`}
              >
                <Typography
                  variant="h5"
                  language={lang}
                  className="line-clamp-3 md:line-clamp-2 font-semibold !text-base md:!text-[18px]"
                >
                  {title}
                </Typography>
              </Link>
              <div className="hidden gap-4 mt-4 uppercase divide-x-2 divide-black md:flex dark:divide-white">
                <Link
                  href={`/${categoryId?.slug}`}
                  aria-label={`View all news in the category: ${getCategoryDisplayName(categoryId, lang) || categoryId?.slug}`}
                >
                  <Typography className="font-medium" variant="body2">
                    {getCategoryDisplayName(categoryId, lang) || categoryId?.slug}
                  </Typography>
                </Link>
                <Link
                  href={`/${categoryId?.slug}/${subCategoryId?.slug}`}
                  aria-label={`View all news in the sub-category: ${getCategoryDisplayName(subCategoryId, lang) || subCategoryId?.slug}`}
                >
                  <Typography
                    className="pl-4 text-secondaryText dark:text-darkBgSecondaryColor"
                    variant="body2"
                  >
                    {getCategoryDisplayName(subCategoryId, lang) || subCategoryId?.slug}
                  </Typography>
                </Link>
              </div>

              <div className="flex items-center justify-between gap-4 mt-4">
                <span className="block text-[#858585] text-sm uppercase">
                  {formatDate(publishedAt, lang)}
                </span>

                <div className="hidden md:block">
                  <SocialShare
                    title={title}
                    url={`/interviews/${slug}`}
                  />
                </div>
                <div className="md:hidden">
                  <SocialShare
                    title={title}
                    url={`/interviews/${slug}`}
                    customButton={
                      <button className="flex items-center justify-center text-gray-600 dark:text-white" aria-label="Share">
                        <IoMdShare />
                      </button>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {showLoadMore && (
        <div className="flex items-center justify-center mt-8">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={isFetching}
            aria-label={getLabel(lang, "LOAD_MORE")}
          >
            {getLabel(lang, "LOAD_MORE")}
          </button>
        </div>
      )}
    </div>
  );
}
